/**
 * GlobalView — USGS Earthquake Client
 *
 * Fetches real-time earthquake data from USGS GeoJSON Summary Feed.
 * https://earthquake.usgs.gov/earthquakes/feed/v1.0/geojson.php
 *
 * Maps GeoJSON Feature[] → Entity[] for the entity store.
 * No API key required — USGS feeds are fully public.
 */

import type { Entity } from '@globalview/shared';

// ─── Feed Types ──────────────────────────────────────────────────

export type USGSFeed =
  | 'significant_hour'
  | 'significant_day'
  | 'significant_week'
  | 'significant_month'
  | 'all_hour'
  | 'all_day'
  | 'all_week'
  | 'all_month'
  | '4.5_day'
  | '4.5_week'
  | '2.5_day'
  | '2.5_week'
  | '1.0_day'
  | '1.0_week';

/** Base URL for USGS GeoJSON feeds */
const USGS_BASE = 'https://earthquake.usgs.gov/earthquakes/feed/v1.0/summary';

/** Map feed key → USGS filename */
const FEED_MAP: Record<USGSFeed, string> = {
  significant_hour: 'significant_hour.geojson',
  significant_day: 'significant_day.geojson',
  significant_week: 'significant_week.geojson',
  significant_month: 'significant_month.geojson',
  all_hour: 'all_hour.geojson',
  all_day: 'all_day.geojson',
  all_week: 'all_week.geojson',
  all_month: 'all_month.geojson',
  '4.5_day': '4.5_day.geojson',
  '4.5_week': '4.5_week.geojson',
  '2.5_day': '2.5_day.geojson',
  '2.5_week': '2.5_week.geojson',
  '1.0_day': '1.0_day.geojson',
  '1.0_week': '1.0_week.geojson',
};

// ─── USGS GeoJSON Types ─────────────────────────────────────────

interface USGSFeature {
  type: 'Feature';
  id: string;
  properties: {
    mag: number | null;
    place: string | null;
    time: number;           // epoch ms
    updated: number;
    url: string;
    detail: string;
    felt: number | null;
    cdi: number | null;     // community decimal intensity
    mmi: number | null;     // modified mercalli intensity
    alert: string | null;   // green, yellow, orange, red
    status: string;         // automatic, reviewed, deleted
    tsunami: number;        // 0 or 1
    sig: number;            // significance 0–1000
    net: string;
    code: string;
    ids: string;
    sources: string;
    types: string;
    nst: number | null;
    dmin: number | null;
    rms: number | null;
    gap: number | null;
    magType: string | null;
    type: string;           // earthquake, quarry blast, etc.
    title: string;
  };
  geometry: {
    type: 'Point';
    coordinates: [number, number, number]; // [lon, lat, depth_km]
  };
}

interface USGSResponse {
  type: 'FeatureCollection';
  metadata: {
    generated: number;
    url: string;
    title: string;
    status: number;
    api: string;
    count: number;
  };
  features: USGSFeature[];
}

// ─── Fetch ───────────────────────────────────────────────────────

/**
 * Fetch earthquake data from USGS.
 * Default feed: `all_day` (all earthquakes in the last 24 hours).
 */
export async function fetchEarthquakes(feed: USGSFeed = 'all_day'): Promise<Entity[]> {
  const filename = FEED_MAP[feed];
  const url = `${USGS_BASE}/${filename}`;

  console.log(`[USGS] Fetching earthquakes: ${feed}...`);

  try {
    const res = await fetch(url);
    if (!res.ok) {
      console.error(`[USGS] HTTP ${res.status} for ${url}`);
      return [];
    }

    const data: USGSResponse = await res.json();

    if (!data.features || data.features.length === 0) {
      console.warn('[USGS] Empty response — no earthquakes');
      return [];
    }

    const entities = data.features
      .filter((f) => f.properties.type === 'earthquake') // skip quarry blasts etc.
      .map(featureToEntity);

    console.log(`[USGS] Loaded ${entities.length} earthquakes (feed: ${feed})`);
    return entities;
  } catch (err) {
    console.error('[USGS] Fetch failed:', err);
    return [];
  }
}

// ─── Mapping ─────────────────────────────────────────────────────

function featureToEntity(feature: USGSFeature): Entity {
  const { properties: p, geometry: g, id } = feature;
  const [lon, lat, depthKm] = g.coordinates;
  const mag = p.mag ?? 0;

  return {
    id: `usgs-${id}`,
    type: 'earthquake',
    source: 'usgs',
    position: {
      lat,
      lon,
      alt: 0, // earthquakes are at/below ground
    },
    heading: undefined,
    timestamp: new Date(p.time).toISOString(),
    style: {
      color: magnitudeToColor(mag),
      size: magnitudeToSize(mag),
    },
    properties: {
      magnitude: mag,
      magType: p.magType ?? 'unknown',
      place: p.place ?? 'Unknown location',
      depth: depthKm,
      tsunami: p.tsunami,
      alert: p.alert,
      significance: p.sig,
      url: p.url,
      felt: p.felt,
      mmi: p.mmi,
      status: p.status,
    },
    confidence: p.status === 'reviewed' ? 1.0 : 0.7,
  };
}

// ─── Magnitude Helpers ───────────────────────────────────────────

/**
 * Map magnitude → CSS color string.
 * Uses a dramatic seismic color ramp.
 */
export function magnitudeToColor(mag: number): string {
  if (mag < 1) return '#22cc88';       // micro — teal-green
  if (mag < 2) return '#44dd66';       // minor — green
  if (mag < 3) return '#aadd22';       // light — yellow-green
  if (mag < 4) return '#ffcc00';       // moderate — amber
  if (mag < 5) return '#ff8800';       // strong — orange
  if (mag < 6) return '#ff4400';       // major — red-orange
  if (mag < 7) return '#ee0000';       // severe — red
  return '#cc0044';                     // great — deep crimson
}

/**
 * Map magnitude → point size multiplier.
 * Exponential scaling for dramatic visual contrast.
 */
export function magnitudeToSize(mag: number): number {
  if (mag < 0) return 1;
  // size = 2 ^ (mag * 0.45) → M1=1.4, M3=2.6, M5=4.9, M7=9.2, M9=17.1
  return Math.pow(2, mag * 0.45);
}
